import React from 'react';
import { ArrowLeft, Pencil, Check, X, Trash2, ExternalLink, GitMerge } from 'lucide-react';
import { Lead, contactDisplayName, contactKindLabel } from '../../../../types';
import { AddressAutocompleteInput } from '../../../../components/AddressAutocompleteInput';
import { AppMultiSelectDropdown } from '../../../../components/AppMultiSelectDropdown';
import { ModuleLink } from '../../../../components/ModuleLink';
import { SiteContactPicker } from '../SiteContactPicker';
import { LeadCustomerPicker } from '../LeadCustomerPicker';
import { contactDetailPath, organizationDetailPath } from '../../../../utils/contactRoutes';
import {
  LEAD_DETAIL_INPUT_CLASS,
  LEAD_DETAIL_LABEL_CLASS,
  LEAD_DETAIL_TEXTAREA_CLASS,
} from './leadDetailStyles';
import type { UseLeadDetailLeadResult } from './useLeadDetailLead';
import type { InstanceMemberOption } from './useLeadActivities';
import { LeadIncomingInvoicesCard } from './LeadIncomingInvoicesCard';
import { LeadWorkReportsCard } from './LeadWorkReportsCard';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';

type MobileSection = 'details' | 'zusammenfassung' | 'angebot' | 'dokumente' | 'kosten';

const SectionCard: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  action,
  children,
}) => (
  <section className="shrink-0 py-4 first:pt-3 border-b border-app-border last:border-b-0">
    <header className="mb-3 flex items-center justify-between gap-2">
      <h2 className="text-[13px] font-semibold text-app-text min-w-0 truncate">{title}</h2>
      <div className="shrink-0 flex items-center gap-1">{action}</div>
    </header>
    <div>{children}</div>
  </section>
);

const DataRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-[100px_1fr] sm:gap-3 py-2 sm:items-baseline">
    <span className="text-[13px] font-medium text-app-text-subtle">{label}</span>
    <span className="text-[13px] text-app-text leading-snug break-words min-w-0">{children}</span>
  </div>
);

const EditToggle: React.FC<{
  editing: boolean;
  label: string;
  isSaving: boolean;
  onStart: () => void;
  onSave: () => void;
  onCancel: () => void;
}> = ({ editing, label, isSaving, onStart, onSave, onCancel }) =>
  editing ? (
    <>
      <button
        type="button"
        onClick={onSave}
        disabled={isSaving}
        className="inline-flex items-center justify-center rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
        aria-label={`${label} speichern`}
        title="Speichern"
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={isSaving}
        className="inline-flex items-center justify-center rounded p-1 text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted disabled:opacity-40"
        aria-label="Bearbeitung abbrechen"
        title="Abbrechen"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </>
  ) : (
    <button
      type="button"
      onClick={onStart}
      className="inline-flex items-center justify-center rounded p-1 text-gray-400 hover:bg-app-hover hover:text-app-text-muted"
      aria-label={`${label} bearbeiten`}
      title={`${label} bearbeiten`}
    >
      <Pencil className="w-3.5 h-3.5" />
    </button>
  );

/** Linke Spalte der Projekt-Detailseite: Auftraggeber, Projektleiter, Baustelle (inkl. Karte). */
export function LeadInfoColumn({
  lead,
  leadApi,
  instanceMembers,
  activeMobileSection,
  setActiveMobileSection,
  baustelleMapAddress,
  baustelleReadOnlyLine,
  baustelleDescriptionHtml,
  onDeleteProject,
  onMergeProject,
  isDeletingProject = false,
  deleteProjectError = null,
}: {
  lead: Lead;
  leadApi: UseLeadDetailLeadResult;
  instanceMembers: InstanceMemberOption[];
  activeMobileSection: MobileSection;
  setActiveMobileSection: React.Dispatch<React.SetStateAction<MobileSection>>;
  baustelleMapAddress: string;
  baustelleReadOnlyLine: string;
  baustelleDescriptionHtml: string;
  onDeleteProject: () => void;
  /** „In anderes Projekt überführen“; fehlt ohne Bearbeiten-Recht. */
  onMergeProject?: () => void;
  isDeletingProject?: boolean;
  deleteProjectError?: string | null;
}) {
  const {
    isSaving,
    editingContact,
    contactSaveError,
    customerPickerRef,
    startEditContact,
    cancelEditContact,
    saveContact,
    leadAssigneeUserIds,
    setLeadAssigneeUserIds,
    saveLeadAssignees,
    editingSiteAddress,
    startEditSiteAddress,
    cancelEditSiteAddress,
    saveSiteAddress,
    siteAddressInput,
    setSiteAddressInput,
    siteDescriptionInput,
    setSiteDescriptionInput,
    siteBestellnummerInput,
    setSiteBestellnummerInput,
    siteAddressSaveError,
    setSiteAddressSaveError,
    siteContactDraft,
    setSiteContactDraft,
    displayIdDraft,
    setDisplayIdDraft,
  } = leadApi;

  const canEditProjectNumber = lead.numbering_manual_edit_enabled !== false;
  const contactNr = (lead.contact?.customer_number ?? '').trim();
  const orgNr = (lead.organization?.customer_number ?? '').trim();
  const auftraggeberArt = (() => {
    if (lead.organization?.organization_type) return lead.organization.organization_type;
    if (lead.organization) return 'Gewerblicher Kunde';
    if (lead.contact) {
      const kind = contactKindLabel(lead.contact);
      if (kind === 'Privatkunde') return 'Privatkunde';
      if (kind) return 'Gewerblicher Kunde';
    }
    return '—';
  })();
  const assigneeNames = leadAssigneeUserIds
    .map((id) => {
      const m = instanceMembers.find((x) => x.user_id === id);
      return m ? (m.display_name ?? '').trim() || m.email : null;
    })
    .filter(Boolean) as string[];

  const [editingAssignees, setEditingAssignees] = React.useState(false);
  const [assigneeDraft, setAssigneeDraft] = React.useState<string[]>([]);
  const [assigneeSaveError, setAssigneeSaveError] = React.useState<string | null>(null);

  const startEditAssignees = () => {
    setAssigneeDraft(leadAssigneeUserIds);
    setAssigneeSaveError(null);
    setEditingAssignees(true);
  };
  const cancelEditAssignees = () => {
    setEditingAssignees(false);
    setAssigneeDraft(leadAssigneeUserIds);
    setAssigneeSaveError(null);
  };
  const saveAssigneesEdit = async () => {
    setAssigneeSaveError(null);
    const ok = await saveLeadAssignees(assigneeDraft);
    if (!ok) {
      setAssigneeSaveError('Projektleiter konnten nicht gespeichert werden.');
      return;
    }
    setLeadAssigneeUserIds(assigneeDraft);
    setEditingAssignees(false);
  };

  const [sidebarTab, setSidebarTab] = React.useState<'info' | 'controlling'>('info');
  const [controllingMounted, setControllingMounted] = React.useState(false);
  // Controlling lebt von den Rechnungen des Projekts.
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const showControlling = modulesLoaded && isEnabled('rechnungen');

  React.useEffect(() => {
    if (sidebarTab === 'controlling') setControllingMounted(true);
  }, [sidebarTab]);

  React.useEffect(() => {
    if (!showControlling) setSidebarTab('info');
  }, [showControlling]);

  return (
    <aside
      className={`${
        activeMobileSection === 'details' ? 'flex w-full' : 'hidden'
      } md:flex md:h-full md:w-64 lg:w-72 xl:w-80 shrink-0 flex-col border-r border-app-border bg-app-surface overflow-hidden min-h-0`}
    >
      {activeMobileSection === 'details' && (
        <button
          type="button"
          onClick={() => setActiveMobileSection('zusammenfassung')}
          className="md:hidden shrink-0 flex items-center gap-2 px-3 text-sm font-medium text-app-text-muted hover:text-app-text py-1 mb-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zur Übersicht
        </button>
      )}

      <div className="shrink-0 flex border-b border-app-border px-3 lg:px-4">
        {(
          [
            { id: 'info' as const, label: 'Infos' },
            { id: 'controlling' as const, label: 'Controlling' },
          ] as const
        ).filter(({ id }) => id !== 'controlling' || showControlling).map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setSidebarTab(id)}
            className={`flex-1 h-9 px-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              sidebarTab === id
                ? 'text-pipedrive-blue border-pipedrive-blue'
                : 'text-app-text-muted border-transparent hover:text-app-text'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <div className={`px-3 pb-3 lg:px-4 lg:pb-4 ${sidebarTab === 'info' ? '' : 'hidden'}`}>
      <SectionCard
        title="Auftraggeber"
        action={
          <EditToggle
            editing={editingContact}
            label="Auftraggeber"
            isSaving={isSaving}
            onStart={startEditContact}
            onSave={() => void saveContact()}
            onCancel={cancelEditContact}
          />
        }
      >
        {editingContact ? (
          <div className="space-y-3">
            <LeadCustomerPicker
              ref={customerPickerRef}
              initialLead={lead}
              disabled={isSaving}
              showOpenLinks
            />
            {contactSaveError ? (
              <p className="text-[11px] leading-snug text-red-600">{contactSaveError}</p>
            ) : null}
          </div>
        ) : (
          <div>
            <DataRow label="Name">
              {(() => {
                const name = contactDisplayName(lead.contact) || '—';
                const contactId = lead.contact?.id ?? lead.contact_id;
                if (name === '—' || contactId == null || contactId === '') return name;
                return (
                  <ModuleLink
                    to={contactDetailPath(contactId)}
                    className="hover:text-pipedrive-blue hover:underline"
                  >
                    {name}
                  </ModuleLink>
                );
              })()}
            </DataRow>
            <DataRow label="Art">{auftraggeberArt}</DataRow>
            {auftraggeberArt === 'Privatkunde' ? null : (
              <DataRow label="Unternehmen">
                {(() => {
                  const company =
                    (lead.organization?.name ?? '').trim() ||
                    (lead.contact?.company ?? '').trim() ||
                    '—';
                  const orgId = lead.organization?.id;
                  if (company === '—' || !orgId) return company;
                  return (
                    <ModuleLink
                      to={organizationDetailPath(orgId)}
                      className="hover:text-pipedrive-blue hover:underline"
                    >
                      {company}
                    </ModuleLink>
                  );
                })()}
              </DataRow>
            )}
            {contactNr ? (
              <DataRow label="Kundennr.">
                <span className="font-mono tabular-nums">{contactNr}</span>
              </DataRow>
            ) : null}
            {orgNr && orgNr !== contactNr ? (
              <DataRow label="Org.-Nr.">
                <span className="font-mono tabular-nums">{orgNr}</span>
              </DataRow>
            ) : null}
            <DataRow label="E-Mail">
              {(lead.email ?? '').trim() ? (
                <a href={`mailto:${lead.email}`} className="hover:text-pipedrive-blue break-all">
                  {lead.email}
                </a>
              ) : (
                '—'
              )}
            </DataRow>
            <DataRow label="Telefon">
              {(lead.phone ?? '').trim() ? (
                <a href={`tel:${lead.phone}`} className="hover:text-pipedrive-blue">
                  {lead.phone}
                </a>
              ) : (
                '—'
              )}
            </DataRow>
            <DataRow label="Adresse">{(lead.contact?.address ?? '').trim() || '—'}</DataRow>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Projektleiter"
        action={
          <EditToggle
            editing={editingAssignees}
            label="Projektleiter"
            isSaving={isSaving}
            onStart={startEditAssignees}
            onSave={() => void saveAssigneesEdit()}
            onCancel={cancelEditAssignees}
          />
        }
      >
        {editingAssignees ? (
          <div className="space-y-2">
            <AppMultiSelectDropdown
              label="Projektleiter"
              placeholder="— Kein Projektleiter —"
              selected={assigneeDraft}
              onChange={(ids) => {
                setAssigneeSaveError(null);
                setAssigneeDraft(ids);
              }}
              options={instanceMembers.map((m) => ({
                value: m.user_id,
                label: (m.display_name ?? '').trim() || m.email || m.user_id.slice(0, 8),
              }))}
              emptyLabel="Keine Mitglieder"
              variant="form"
              className="w-full"
            />
            {assigneeSaveError ? (
              <p className="text-[11px] leading-snug text-red-600">{assigneeSaveError}</p>
            ) : null}
          </div>
        ) : (
          <p className={`text-[13px] ${assigneeNames.length > 0 ? 'text-app-text' : 'text-gray-400'}`}>
            {assigneeNames.length > 0 ? assigneeNames.join(', ') : 'Noch keine Projektleiter zugewiesen.'}
          </p>
        )}
      </SectionCard>

      <SectionCard
        title="Baustelle"
        action={
          <EditToggle
            editing={editingSiteAddress}
            label="Baustelle"
            isSaving={isSaving}
            onStart={startEditSiteAddress}
            onSave={() => void saveSiteAddress()}
            onCancel={cancelEditSiteAddress}
          />
        }
      >
        {editingSiteAddress ? (
          <div className="space-y-3">
            <div>
              <label className={LEAD_DETAIL_LABEL_CLASS} htmlFor="lead-detail-display-id">
                Projektnr.
              </label>
              {canEditProjectNumber ? (
                <input
                  id="lead-detail-display-id"
                  type="text"
                  value={displayIdDraft}
                  onChange={(e) => {
                    setSiteAddressSaveError(null);
                    setDisplayIdDraft(e.target.value);
                  }}
                  className={`${LEAD_DETAIL_INPUT_CLASS} font-mono tabular-nums`}
                  spellCheck={false}
                  autoComplete="off"
                  disabled={isSaving}
                />
              ) : (
                <p className="mt-1 text-[13px] font-mono tabular-nums text-app-text">
                  {lead.display_id || '—'}
                </p>
              )}
            </div>
            <div>
              <label className={LEAD_DETAIL_LABEL_CLASS}>Baustellenkontakt</label>
              <p className="mb-1.5 text-[11px] leading-snug text-app-text-subtle">
                Ansprechpartner für den Monteur vor Ort. Rechnung bleibt beim Projektkontakt.
              </p>
              <SiteContactPicker
                value={siteContactDraft}
                disabled={isSaving}
                onSelect={(c) => setSiteContactDraft(c)}
              />
              {siteContactDraft?.id != null && siteContactDraft.id !== '' ? (
                <div className="mt-1.5">
                  <ModuleLink
                    to={contactDetailPath(siteContactDraft.id)}
                    className="inline-flex items-center gap-1 text-[12px] font-medium text-pipedrive-blue hover:underline"
                  >
                    Kontakt öffnen & bearbeiten
                    <ExternalLink className="h-3 w-3" aria-hidden />
                  </ModuleLink>
                </div>
              ) : null}
            </div>
            <div>
              <label className={LEAD_DETAIL_LABEL_CLASS} htmlFor="lead-detail-site-address">
                Adresse Baustelle
              </label>
              <AddressAutocompleteInput
                id="lead-detail-site-address"
                value={siteAddressInput}
                onChange={(v) => {
                  setSiteAddressSaveError(null);
                  setSiteAddressInput(v);
                }}
                placeholder="Straße, Hausnummer, PLZ Ort"
                inputClassName={LEAD_DETAIL_INPUT_CLASS}
              />
            </div>
            <div>
              <label className={LEAD_DETAIL_LABEL_CLASS} htmlFor="lead-detail-site-bestellnummer">
                Bestellnummer
              </label>
              <input
                id="lead-detail-site-bestellnummer"
                type="text"
                value={siteBestellnummerInput}
                onChange={(e) => {
                  setSiteAddressSaveError(null);
                  setSiteBestellnummerInput(e.target.value);
                }}
                className={LEAD_DETAIL_INPUT_CLASS}
                placeholder="z. B. PO-2026-0042"
              />
            </div>
            <div>
              <label className={LEAD_DETAIL_LABEL_CLASS} htmlFor="lead-detail-site-description">
                Beschreibung
              </label>
              <textarea
                id="lead-detail-site-description"
                value={siteDescriptionInput}
                onChange={(e) => {
                  setSiteAddressSaveError(null);
                  setSiteDescriptionInput(e.target.value);
                }}
                rows={6}
                className={`${LEAD_DETAIL_TEXTAREA_CLASS} resize-y`}
                placeholder="Beschreibung der Baustelle"
              />
            </div>
            {siteAddressSaveError && (
              <p className="text-[11px] leading-snug text-red-600">{siteAddressSaveError}</p>
            )}
          </div>
        ) : (
          <div>
            <DataRow label="Projektnr.">
              <span className="font-mono tabular-nums">{lead.display_id || '—'}</span>
            </DataRow>
            <DataRow label="Kontakt">
              {(() => {
                const siteContact = lead.baustelle?.contact;
                if (!siteContact) return '—';
                const name = contactDisplayName(siteContact) || 'Kontakt';
                const contactId = siteContact.id;
                if (contactId == null || contactId === '') return name;
                return (
                  <ModuleLink
                    to={contactDetailPath(contactId)}
                    className="hover:text-pipedrive-blue hover:underline"
                  >
                    {name}
                  </ModuleLink>
                );
              })()}
            </DataRow>
            {lead.baustelle?.contact ? (
              <>
                <DataRow label="E-Mail">
                  {(lead.baustelle.contact.email ?? '').trim() ? (
                    <a
                      href={`mailto:${lead.baustelle.contact.email}`}
                      className="break-all hover:text-pipedrive-blue"
                    >
                      {lead.baustelle.contact.email}
                    </a>
                  ) : (
                    '—'
                  )}
                </DataRow>
                <DataRow label="Telefon">
                  {(lead.baustelle.contact.phone ?? '').trim() ? (
                    <a
                      href={`tel:${lead.baustelle.contact.phone}`}
                      className="hover:text-pipedrive-blue"
                    >
                      {lead.baustelle.contact.phone}
                    </a>
                  ) : (
                    '—'
                  )}
                </DataRow>
              </>
            ) : null}
            <DataRow label="Adresse">
              <span className="whitespace-pre-wrap">{baustelleReadOnlyLine}</span>
            </DataRow>
            <DataRow label="Bestellnummer">
              <span className="whitespace-pre-wrap">
                {(lead.baustelle?.bestellnummer ?? '').trim() || '—'}
              </span>
            </DataRow>
            <DataRow label="Beschreibung">
              {baustelleDescriptionHtml?.trim() ? (
                <div
                  className="leading-relaxed [&_p]:m-0 [&_p+p]:mt-2 [&_ul]:my-1 [&_ol]:my-1"
                  dangerouslySetInnerHTML={{ __html: baustelleDescriptionHtml }}
                />
              ) : (
                '—'
              )}
            </DataRow>
          </div>
        )}
        {baustelleMapAddress ? (
          <div className="mt-3 hidden overflow-hidden rounded-md border border-app-border bg-app-surface-muted md:block">
            <iframe
              title="Karte Baustelle"
              src={`https://www.google.com/maps?q=${encodeURIComponent(baustelleMapAddress)}&output=embed`}
              className="w-full h-40 border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        ) : (
          <p className="mt-3 hidden text-[11px] text-app-text-subtle md:block">
            Tragen Sie eine Adresse ein – die Karte erscheint automatisch.
          </p>
        )}
      </SectionCard>

      <div className="mt-4 space-y-2 pt-1">
        {onMergeProject ? (
          <button
            type="button"
            onClick={onMergeProject}
            disabled={isDeletingProject}
            className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md border border-app-border bg-app-surface px-3.5 text-[13px] text-app-text-muted transition-colors hover:bg-app-hover disabled:opacity-60"
          >
            <GitMerge className="h-3.5 w-3.5 shrink-0" />
            In anderes Projekt überführen
          </button>
        ) : null}
        <button
          type="button"
          onClick={onDeleteProject}
          disabled={isDeletingProject}
          className="app-btn-danger-outline inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md border border-red-200 bg-app-surface px-3.5 text-[13px] text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
          aria-label="Projekt löschen"
        >
          <Trash2 className="h-3.5 w-3.5 shrink-0" />
          Projekt löschen
        </button>
        {deleteProjectError ? (
          <p className="text-[11px] leading-snug text-red-600">{deleteProjectError}</p>
        ) : null}
      </div>
      </div>
      {controllingMounted && showControlling ? (
        <div
          // Full-height column so the loading state can sit in the middle of the tab.
          className={`px-3 pb-3 lg:px-4 lg:pb-4 ${sidebarTab === 'controlling' ? 'flex min-h-full flex-col' : 'hidden'}`}
        >
          <LeadIncomingInvoicesCard key={String(lead.id)} leadId={String(lead.id)} variant="sidebar" />
          <LeadWorkReportsCard key={`work-reports-${String(lead.id)}`} leadId={String(lead.id)} variant="sidebar" />
        </div>
      ) : null}
      </div>
    </aside>
  );
}
